import { SeenThingsPresenter } from '@Components/Modals/SeenThings/SeenThingsPresenter';
import type { FloorDM } from '@Model/FloorModels';
import { useClassStore } from '@Utils/useClassStore';
import { inject, injectable, postConstruct } from 'inversify';
import { action, computed, observable } from 'mobx';

import { BbContentPresenter } from './BbContentPresenter';

import container from '~ioc/ioc';
import { FloorsListStore } from '~stores/floors-list';
import { NavigationStore } from '~stores/navigation';
import type { JourneyParameters } from '~types/navigation';

@injectable()
export class BbQueueManager {
  @inject(NavigationStore) private _navigator2Store!: NavigationStore;
  @inject(FloorsListStore) private _floorsStore2!: FloorsListStore;
  @inject(SeenThingsPresenter)
  private _seenThingsPresenter!: SeenThingsPresenter;

  @postConstruct() postConstruct() {
    this._floorsStore2.bbQueueManager.set(this); // Solves circular dependency injection
    this._seenThingsPresenter.bindBBQueueManager(this);
  }

  presenters = observable.map<string, BbContentPresenter>({});

  getContentPresenter = (
    journeyParams: JourneyParameters,
    routeKey: string,
    scrollAtLaunch = true
  ) => {
    const presenter = container.get(BbContentPresenter);
    presenter.init(journeyParams, routeKey, scrollAtLaunch).then();

    this.presenters.set(routeKey, presenter);

    return presenter;
  };

  @action unregisterPresenter = (routeKey: string) => {
    this.presenters.delete(routeKey);
  };

  @computed get stack() {
    return this._navigator2Store.navState.value?.routes ?? [];
  }

  @computed get root(): BbContentPresenter | undefined {
    return this.presenters.get(this.stack[0]?.key);
  }

  @computed get presentersArray() {
    return Array.from(this.presenters.values());
  }

  handleEmptyJourneyParam = (floorId: FloorDM['id']) => {
    const routes = this._navigator2Store.getState()?.routes;

    if (routes?.length === 1) {
      // Landing on default floor
      this._navigator2Store.navToFloor(floorId);
    } else {
      // Landing via a deeplink

      // TODO / this behaviour here should change and redirect to
      // correct floorId when the deeplink content is actually fetched
      // Needs API alterations
      this._navigator2Store.replaceRootFloor(floorId);
    }
  };
}

export const useBbQueueManager = () =>
  useClassStore<BbQueueManager>(container.get(BbQueueManager));
